import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Link,
  Spinner,
  Stack,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import SidebarWithHeader from 'components/SidebarWithHeader';
import moment from 'moment';
import { FiPhoneCall } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useGetPurchaseNumberByEmailQuery } from 'store/aiHub/externalAiApi';

const CONVERSATION_BASE_URL = process.env.REACT_APP_CONVERSATION_BASE_URL;

function LabelValue({ label, children }) {
  return (
    <Text mb={1}>
      <Text as="span" fontWeight="bold">{label}:</Text>{' '}
      <Text as="span">{children}</Text>
    </Text>
  );
}

export default function CallCenter() {

  const userInfo = useSelector((s) => s.auth?.userInfo);
  const email = userInfo?.email;
  const { data, isFetching, isError, error } = useGetPurchaseNumberByEmailQuery(email ?? skipToken);

  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const textColorSub = useColorModeValue("brand.300", "brand.700");

  return (
    <SidebarWithHeader>
      <Box p={2}>
        {isFetching ? (
          <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'70vh'}>
            <Spinner />
            <Text>Loading…</Text>
          </Flex>
        ) : isError ? (
          <Text color={'red.500'}>{error?.error || 'Failed to load data'}</Text>
        ) : (
          <>
            {/* Header */}
            <Flex
              direction={{ base: "column", md: "row" }}
              align={{ base: "stretch", md: "start" }}
              justify="space-between"
              mb={6}
              gap={4}
            >
              <Box flex="1">
                <Heading size="lg" mb={2}>
                  Conversation: AI Call Center
                </Heading>
                <Text fontSize="14px" color={textColorSub}>
                  Transform business calls with AI-powered pathways by unifying number management, flow builder, inbound/outbound calling, and call logging in one system. Built-in lead filtering ensures only high-intent leads reach sales teams, driving smarter optimisation, targeting, and ROI.
                </Text>
                {data?.phoneNumbers?.length > 0 && (
                  <Text fontSize="md">
                    <Text as="span" fontWeight="bold">Purchased numbers:</Text>{" "}
                    <Text as="span">{data.count ?? 0}</Text>
                  </Text>
                )}
              </Box>
              
              {data?.phoneNumbers?.length > 0 && (
                <Button
                  as={Link}
                  href={`${CONVERSATION_BASE_URL}/authenticateHustle?token=${userInfo?.token}`}
                  leftIcon={<FiPhoneCall />}
                  colorScheme="brand"
                  size="sm"
                  isExternal
                  mt={{ base: 4, md: 0 }} 
                >
                  AI Call Center
                </Button>
              )}
            </Flex>

            {/* Numbers */}
            <Flex w="100%" justify="center">
              {(data?.phoneNumbers ?? []).length === 0 ? (
                <Flex
                  direction="column"
                  gap={2}
                  align="center"
                  justify="center"
                  w="100%"
                  h="60vh"
                >
                  <Text fontSize="sm" textAlign="center">
                    To get started with calls and analytics, please purchase a phone number.
                  </Text>
                  <Button
                    as={Link}
                    href={`${CONVERSATION_BASE_URL}/authenticateHustle?token=${userInfo?.token}`}
                    leftIcon={<FiPhoneCall />}
                    colorScheme="brand"
                    size="sm"
                    isExternal
                    mt={{ base: 4, md: 0 }} 
                  >
                    AI Call Center
                </Button>
                </Flex>
              ) : (
                <Flex wrap="wrap" gap={4} justify="flex-start" w="100%">
                  {(data?.phoneNumbers ?? []).map((n) => {
                    const purchasedAt = n.purchased_at
                      ? moment(n.purchased_at).format('M/D/YYYY, h:mm:ss A')
                      : '-';
                    const statusColor =
                      n.status === 'active'
                        ? 'green'
                        : n.status === 'inactive'
                        ? 'gray'
                        : 'orange';

                    return (
                      <Box
                        key={n.id}
                        p={6}
                        bg={cardBg}
                        border="1px solid"
                        borderColor={cardBorder}
                        borderRadius="md"
                        flex="0 0 auto"      
                        maxW="500px"         
                        w="100%"            
                      >
                        <Stack spacing={2} mb={4}>
                          <LabelValue label="Number">{n.number}</LabelValue>
                          <LabelValue label="Location">{n.location || '-'}</LabelValue>
                          <LabelValue label="Type">{n.type || '-'}</LabelValue>
                          <LabelValue label="Purchased At">{purchasedAt}</LabelValue>
                          <LabelValue label="Monthly Fee">
                            {typeof n.monthly_fee === 'number' ? `$${n.monthly_fee}` : '-'}
                          </LabelValue>
                          <LabelValue label="Pathway">
                            {n.pathway_name || n.pathway_id || '-'}
                          </LabelValue>
                        </Stack>

                        <HStack spacing={3}>
                          <Badge colorScheme={statusColor} variant="subtle" px={3} py={1}>
                            {n.status || 'unknown'}
                          </Badge>
                        </HStack>
                      </Box>
                    );
                  })}
                </Flex>
              )}
            </Flex>

          </>
        )}
      </Box>
    </SidebarWithHeader>
  );
}
